import React from "react";
import {autobind} from "core-decorators";
import {Button, message, Popconfirm, Tooltip} from "antd";
import {FaTrash} from "react-icons/fa";
import {runInAction} from "mobx";
import {inject, observer} from "mobx-react";
import {gql} from "@apollo/client";

import "./EnsemblesActions.scss";
import {DeleteEnsemble, DeleteEnsembleVariables, EnsemblesQuery} from "@/graphql/types";
import Store from "@/stores";

export interface IEnsemblesActionsProps {
  ensemble: EnsemblesQuery["ensembles"][0];
  ensembles: EnsemblesQuery["ensembles"];
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class EnsemblesActions extends React.Component<IEnsemblesActionsProps> {
  async delete() {
    const {ensemble, ensembles} = this.props;
    const ensembleIdx = ensembles.indexOf(ensemble);
    runInAction(() => ensembles.splice(ensembleIdx, 1)); // optimistic UI update
    try {
      const result = await this.props.store!.apollo.client.mutate<DeleteEnsemble, DeleteEnsembleVariables>({
        mutation: gql`
          mutation DeleteEnsemble($id: Int!) {
            delete_rehearsals(where: {part: {piece: {choir_id: {_eq: $id}}}}) {
              affected_rows
            }
            delete_parts(where: {piece: {choir_id: {_eq: $id}}}) {
              affected_rows
            }
            delete_pieces(where: {choir_id: {_eq: $id}}) {
              affected_rows
            }
            delete_user_choirs(where: {choir_id: {_eq: $id}}) {
              affected_rows
            }
            delete_user_invitations(where: {choir_id: {_eq: $id}}) {
              affected_rows
            }
            ensemble: delete_choirs_by_pk(id: $id) {
              id
            }
          }
        `,
        variables: {id: ensemble.id},
      });
      const deletedEnsemble = result.data?.ensemble;
      if (!deletedEnsemble) throw new Error("Could not delete Ensemble");
      await this.props.store!.session.fetchSessionData(); // refresh ensembles in the session
    } catch (error) {
      runInAction(() => ensembles.splice(ensembleIdx, 0, ensemble));
      console.error(error);
      message.error("Oops! Error while deleting ensemble");
    }
  }

  public render() {
    const {ensembles} = this.props;
    const deleteEnabled = ensembles.length > 1;
    return (
      <div className="EnsemblesActions">
        <Tooltip
          placement="topLeft"
          arrowPointAtCenter
          title={deleteEnabled ? "Delete" : "Delete is disabled because having at least 1 ensemble is required"}
        >
          <Popconfirm
            title={
              <span>
                You are going to delete this ensemble and all its pieces and rehearsals.
                <br />
                This action is not reversible. Do you want to proceed?
              </span>
            }
            placement="topRight"
            arrowPointAtCenter
            onConfirm={this.delete}
            disabled={!deleteEnabled}
          >
            <Button type="link" shape="circle" size="small" disabled={!deleteEnabled}>
              <FaTrash />
            </Button>
          </Popconfirm>
        </Tooltip>
      </div>
    );
  }
}
